import { Ionicons } from "@expo/vector-icons";
import React from "react";
import { View, Text, Image, StyleSheet, Platform, Dimensions } from "react-native";

import avatarDefault from "../../../../assets/avatar-default.png";
import { t } from "../../../i18n/translations";
import { useLanguageStore } from "../../quiz-core/languageStore";

const { width: screenWidth } = Dimensions.get("window");

const IQES = {
  panel: "#0B1026",
  panelDeep: "#070A18",
  panelLift: "#151B37",
  gold: "#F8C84E",
  goldSoft: "#FFE78A",
  purpleDeep: "#21103D",
  ivory: "#FFF7DF",
  text: "#F9F0D6",
  muted: "#C9BEDA",
  border: "rgba(248, 200, 78, 0.42)",
  borderStrong: "rgba(248, 200, 78, 0.72)",
  green: "#7FE08E",
  blue: "#A9A7FF",
};

const scaleFont = (size: number) => {
  const scale = screenWidth / 375;
  const nextSize = size * scale;
  return Math.max(nextSize, size * 0.88);
};

interface MultiplayerPlayerCardProps {
  player: {
    avatar_url?: string | null;
    correct?: number | null;
    name?: string | null;
    nickname?: string | null;
    score?: number | null;
    time?: string | number | null;
    totalQuestions?: number | null;
    user_id?: string | null;
  };
  index: number;
  profile: { id?: string | null };
  badgeLotties?: Record<string, unknown>;
}

const MultiplayerPlayerCard: React.FC<MultiplayerPlayerCardProps> = ({
  player,
  index,
  profile,
}) => {
  const { language } = useLanguageStore();
  const isUser = player.user_id === profile.id;
  const isWinnerCard = index === 0;
  const isPerfectScore = player.correct !== undefined && player.correct === player.totalQuestions;
  const displayName = player.name || player.nickname || t("results.player", language);
  const scoreValue = typeof player.score === "number" ? player.score : 0;
  const correctValue = typeof player.correct === "number" ? player.correct : 0;
  const totalQuestionsValue = typeof player.totalQuestions === "number" && player.totalQuestions > 0 ? player.totalQuestions : 10;
  const accuracyValue = Math.round((correctValue / totalQuestionsValue) * 100);
  const hasTime = player.time !== undefined && player.time !== null && player.time !== "";

  if (isWinnerCard) {
    return (
      <View
        key={player.user_id}
        style={[
          styles.heroCard,
          {
            backgroundColor: IQES.panel,
            borderColor: isUser ? IQES.borderStrong : IQES.borderStrong,
          },
        ]}
        accessibilityLabel={`Player card for ${displayName}, Score: ${scoreValue}, Correct: ${correctValue}, Time: ${player.time || "N/A"}`}
      >
        <View style={styles.heroTopRow}>
          <View style={styles.rankPill}>
            <Ionicons name="trophy" size={14} color={IQES.goldSoft} />
            <Text style={styles.rankPillText}>#1</Text>
          </View>
          {isUser ? (
            <View style={styles.userPill}>
              <Ionicons name="person" size={12} color={IQES.goldSoft} />
            </View>
          ) : null}
        </View>

        <View style={styles.heroMainRow}>
          <View style={[styles.heroAvatarWrap, { borderColor: isUser ? IQES.goldSoft : IQES.borderStrong }]}>
            <Image
              source={player.avatar_url ? { uri: player.avatar_url } : avatarDefault}
              style={styles.heroAvatar}
              accessibilityLabel={`Avatar for ${displayName}`}
            />
          </View>

          <View style={styles.heroInfo}>
            <Text style={styles.heroName}>
              {displayName}
            </Text>
            <View style={styles.heroMetaRow}>
              {isPerfectScore ? (
                <View style={styles.perfectScoreBadge}>
                  <Text style={styles.perfectScoreText}>{t("results.perfectScore", language)}</Text>
                </View>
              ) : null}
              {!isPerfectScore ? (
                <Text style={styles.heroSubline}>
                  {correctValue}/{totalQuestionsValue} · {accuracyValue}%
                </Text>
              ) : null}
            </View>
          </View>

          <View style={styles.heroScoreBlock}>
            <Text style={styles.heroScoreLabel}>
              {t("results.score", language)}
            </Text>
            <Text style={styles.heroScoreValue}>{scoreValue}</Text>
          </View>
        </View>

        <View style={styles.heroStatsRow}>
          <View style={styles.statPill}>
            <Ionicons name="checkmark-circle" size={14} color={IQES.green} />
            <Text style={styles.statPillText}>
              {correctValue}/{totalQuestionsValue}
            </Text>
          </View>
          <View style={styles.statPill}>
            <Ionicons name="speedometer" size={14} color={IQES.blue} />
            <Text style={styles.statPillText}>
              {accuracyValue}%
            </Text>
          </View>
          {hasTime ? (
            <View style={styles.statPill}>
              <Ionicons name="time" size={14} color={IQES.blue} />
              <Text style={styles.statPillText}>
                {player.time}
              </Text>
            </View>
          ) : null}
        </View>
      </View>
    );
  }

  return (
    <View
      key={player.user_id}
      style={[
        styles.cardContainer,
        {
          backgroundColor: isUser ? IQES.panel : IQES.panelDeep,
          borderColor: isUser ? IQES.borderStrong : IQES.border,
        },
      ]}
      accessibilityLabel={`Player card for ${displayName}, Score: ${scoreValue}, Correct: ${correctValue}, Time: ${player.time || "N/A"}`}
    >
      <View style={styles.rowLeft}>
        <View style={styles.rowAvatarWrap}>
          <Image
            source={player.avatar_url ? { uri: player.avatar_url } : avatarDefault}
            style={styles.avatar}
            accessibilityLabel={`Avatar for ${displayName}`}
          />
        </View>
        <View style={styles.infoContainer}>
          <View style={styles.nameRow}>
            <Text style={styles.playerName}>
              {displayName}
            </Text>
            {isUser ? (
              <View style={styles.inlineUserPill}>
                <Ionicons name="person" size={11} color={IQES.goldSoft} />
              </View>
            ) : null}
          </View>
          <View style={styles.inlineStats}>
            <View style={styles.inlineStatItem}>
              <Ionicons name="checkmark-circle" size={12} color={IQES.green} style={styles.statIcon} />
              <Text style={styles.inlineStatText}>{correctValue}/{totalQuestionsValue}</Text>
            </View>
            <View style={styles.inlineStatItem}>
              <Ionicons name="speedometer" size={12} color={IQES.blue} style={styles.statIcon} />
              <Text style={styles.inlineStatText}>{accuracyValue}%</Text>
            </View>
            {hasTime ? (
              <View style={styles.inlineStatItem}>
                <Ionicons name="time" size={12} color={IQES.blue} style={styles.statIcon} />
                <Text style={styles.inlineStatText}>{player.time}</Text>
              </View>
            ) : null}
          </View>
        </View>
      </View>

      <View style={styles.rowRight}>
        <View style={styles.rankBadge}>
          <Text style={styles.rankBadgeText}>#{index + 1}</Text>
        </View>
        <View style={[styles.scoreBadge, { backgroundColor: isUser ? "rgba(248, 200, 78, 0.16)" : IQES.panelLift }]}>
          <Text style={styles.scoreBadgeValue}>
            {scoreValue}
          </Text>
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  heroCard: {
    width: "100%",
    borderRadius: 24,
    borderWidth: 1.5,
    paddingHorizontal: 16,
    paddingTop: 14,
    paddingBottom: 16,
    marginBottom: 12,
    ...Platform.select({
      ios: {
        shadowColor: "#1D1A23",
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.08,
        shadowRadius: 14,
      },
      android: {
        elevation: 5,
      },
      web: {
        boxShadow: "0 8px 24px rgba(29, 26, 35, 0.08)",
      },
    }),
  },
  heroTopRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 14,
  },
  rankPill: {
    flexDirection: "row",
    alignItems: "center",
    borderRadius: 999,
    paddingHorizontal: 10,
    paddingVertical: 6,
    backgroundColor: "rgba(248, 200, 78, 0.15)",
    borderWidth: 1,
    borderColor: IQES.border,
  },
  rankPillText: {
    marginLeft: 6,
    fontSize: 12,
    fontWeight: "800",
    color: IQES.goldSoft,
  },
  userPill: {
    borderRadius: 999,
    paddingHorizontal: 10,
    paddingVertical: 6,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: IQES.purpleDeep,
    borderWidth: 1,
    borderColor: IQES.border,
  },
  heroMainRow: {
    flexDirection: "row",
    alignItems: "center",
  },
  heroAvatarWrap: {
    borderWidth: 2,
    borderRadius: 28,
    padding: 2,
    marginRight: 12,
  },
  heroAvatar: {
    width: 52,
    height: 52,
    borderRadius: 26,
    backgroundColor: "#E5E7EB",
  },
  heroInfo: {
    flex: 1,
    marginRight: 10,
  },
  heroName: {
    fontSize: scaleFont(19),
    fontWeight: "800",
    lineHeight: scaleFont(24),
    color: IQES.ivory,
  },
  heroMetaRow: {
    flexDirection: "row",
    marginTop: 6,
    minHeight: 20,
    alignItems: "center",
    flexWrap: "wrap",
  },
  heroSubline: {
    fontSize: 12,
    fontWeight: "700",
    color: IQES.muted,
  },
  perfectScoreBadge: {
    backgroundColor: "rgba(248, 200, 78, 0.13)",
    borderRadius: 999,
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  perfectScoreText: {
    fontSize: 10,
    fontWeight: "800",
    color: IQES.goldSoft,
    letterSpacing: 0.4,
  },
  heroScoreBlock: {
    minWidth: 72,
    borderRadius: 18,
    paddingHorizontal: 12,
    paddingVertical: 10,
    alignItems: "center",
    backgroundColor: "rgba(248, 200, 78, 0.16)",
    borderWidth: 1,
    borderColor: IQES.border,
  },
  heroScoreLabel: {
    fontSize: 11,
    fontWeight: "800",
    textTransform: "uppercase",
    letterSpacing: 0.7,
    marginBottom: 2,
    color: IQES.goldSoft,
  },
  heroScoreValue: {
    fontSize: scaleFont(28),
    fontWeight: "900",
    lineHeight: scaleFont(32),
    color: IQES.goldSoft,
  },
  heroStatsRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
    marginTop: 14,
  },
  statPill: {
    flexDirection: "row",
    alignItems: "center",
    borderRadius: 999,
    paddingHorizontal: 10,
    paddingVertical: 8,
    backgroundColor: IQES.panelLift,
    borderWidth: 1,
    borderColor: "rgba(255, 247, 223, 0.1)",
  },
  statPillText: {
    marginLeft: 6,
    fontSize: 13,
    fontWeight: "700",
    color: IQES.text,
  },
  cardContainer: {
    width: "100%",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 10,
    paddingHorizontal: 14,
    paddingVertical: 12,
    borderRadius: 20,
    borderWidth: 1,
    ...Platform.select({
      ios: {
        shadowColor: "#1D1A23",
        shadowOffset: { width: 0, height: 3 },
        shadowOpacity: 0.05,
        shadowRadius: 10,
      },
      android: {
        elevation: 3,
      },
      web: {
        boxShadow: "0 6px 18px rgba(29, 26, 35, 0.06)",
      },
    }),
  },
  rowLeft: {
    flexDirection: "row",
    alignItems: "center",
    flex: 1,
    marginRight: 12,
  },
  rowAvatarWrap: {
    marginRight: 12,
  },
  avatar: {
    width: 42,
    height: 42,
    borderRadius: 21,
    backgroundColor: "#E5E7EB",
  },
  infoContainer: {
    flex: 1,
  },
  nameRow: {
    flexDirection: "row",
    alignItems: "center",
    flexWrap: "wrap",
  },
  playerName: {
    fontSize: scaleFont(17),
    fontWeight: "700",
    lineHeight: scaleFont(22),
    color: IQES.ivory,
  },
  inlineUserPill: {
    borderRadius: 999,
    paddingHorizontal: 8,
    paddingVertical: 3,
    marginLeft: 8,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: IQES.purpleDeep,
    borderWidth: 1,
    borderColor: IQES.border,
  },
  inlineStats: {
    flexDirection: "row",
    flexWrap: "wrap",
    marginTop: 6,
  },
  inlineStatItem: {
    flexDirection: "row",
    alignItems: "center",
    marginRight: 12,
  },
  statIcon: {
    marginRight: 4,
  },
  inlineStatText: {
    fontSize: 13,
    fontWeight: "600",
    color: IQES.muted,
  },
  rowRight: {
    alignItems: "flex-end",
    justifyContent: "center",
  },
  rankBadge: {
    minWidth: 46,
    borderRadius: 999,
    paddingHorizontal: 10,
    paddingVertical: 6,
    alignItems: "center",
    marginBottom: 6,
    backgroundColor: IQES.panelLift,
    borderWidth: 1,
    borderColor: "rgba(255, 247, 223, 0.1)",
  },
  rankBadgeText: {
    fontSize: 12,
    fontWeight: "800",
    color: IQES.muted,
  },
  scoreBadge: {
    minWidth: 54,
    borderRadius: 14,
    paddingHorizontal: 12,
    paddingVertical: 8,
    alignItems: "center",
    borderWidth: 1,
    borderColor: IQES.border,
  },
  scoreBadgeValue: {
    fontSize: scaleFont(18),
    fontWeight: "800",
    lineHeight: scaleFont(22),
    color: IQES.goldSoft,
  },
});

export default MultiplayerPlayerCard;
